import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Package, CalendarDays, FileText, BarChart3, ArrowRight, Check, Target, PieChart, Sparkles, Zap, Clock, Play, Receipt, Wallet } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';

const Landing = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('auth_token');
  const operatorName = localStorage.getItem('operator_name');

  const features = [
    {
      icon: Target,
      title: t('landing.feature1Title'),
      description: t('landing.feature1Desc'),
      color: 'text-primary',
      highlight: true
    },
    {
      icon: PieChart,
      title: t('landing.feature2Title'),
      description: t('landing.feature2Desc'),
      color: 'text-secondary',
      highlight: true
    },
    {
      icon: Package,
      title: t('landing.feature3Title'),
      description: t('landing.feature3Desc'),
      color: 'text-accent'
    },
    {
      icon: CalendarDays,
      title: t('landing.feature4Title'),
      description: t('landing.feature4Desc'),
      color: 'text-primary'
    },
    {
      icon: FileText,
      title: t('landing.feature5Title'),
      description: t('landing.feature5Desc'),
      color: 'text-secondary'
    },
    {
      icon: BarChart3,
      title: t('landing.feature6Title'),
      description: t('landing.feature6Desc'),
      color: 'text-accent'
    },
    {
      icon: Receipt,
      title: t('landing.feature7Title'),
      description: t('landing.feature7Desc'),
      color: 'text-primary'
    },
    {
      icon: Wallet,
      title: t('landing.feature8Title'),
      description: t('landing.feature8Desc'),
      color: 'text-secondary'
    }
  ];

  const benefits = [
    t('landing.benefit1'),
    t('landing.benefit2'),
    t('landing.benefit3'),
    t('landing.benefit4'),
    t('landing.benefit5'),
    t('landing.benefit6')
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex flex-col items-start min-w-0">
            <img 
              src="/tourpilot-logo.png" 
              alt="TourPilot.biz" 
              className="h-10 sm:h-12 md:h-14 lg:h-16 max-w-[150px] sm:max-w-[180px] md:max-w-[220px] lg:max-w-[250px] object-contain"
            />
            <span className="text-xs text-muted-foreground mt-1 hidden md:block whitespace-nowrap">
              {t('landing.tagline')}
            </span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <LanguageSelector />
            {isAuthenticated ? (
              <>
                <span className="text-xs sm:text-sm text-muted-foreground hidden lg:inline">{t('common.welcome')}, {operatorName}</span>
                <Button onClick={() => navigate('/app/dashboard')} data-testid="go-to-dashboard" size="sm" className="text-xs sm:text-sm">
                  {t('nav.goToDashboard')}
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-link" size="sm" className="hidden sm:inline-flex text-xs sm:text-sm">
                  {t('common.login')}
                </Button>
                <Button onClick={() => navigate('/login')} data-testid="get-started-button" size="sm" className="text-xs sm:text-sm">
                  {t('landing.getStarted')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5" />
        <div className="container mx-auto px-4 py-12 sm:py-16 md:py-24 lg:py-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div>
              <Badge className="mb-4 sm:mb-6 bg-primary/10 text-primary border-primary/20 text-xs sm:text-sm">
                ✨ {t('landing.first2MonthsFree')}
              </Badge>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-foreground mb-4 sm:mb-6 leading-tight">
                {t('landing.pilotTagline')}
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8">
                {t('landing.pilotSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
                <Button size="lg" onClick={() => navigate('/login')} className="gap-2 w-full sm:w-auto" data-testid="hero-cta-button">
                  {t('landing.startFreeTrial')}
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <Button size="lg" variant="outline" className="gap-2 w-full sm:w-auto">
                  {t('landing.learnMore')}
                </Button>
              </div>
              <p className="mt-4 text-xs sm:text-sm text-muted-foreground">
                {t('landing.noCardRequired')}
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-2xl border border-border">
                <img
                  src="https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&h=600&fit=crop"
                  alt="Tour Operations Dashboard"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simple & Easy Section - No Complex ERP */}
      <section className="py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-3 sm:mb-4 text-xs sm:text-sm">{t('landing.simpleAndEasy')}</Badge>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-extrabold text-foreground mb-3 sm:mb-4">
              {t('landing.noComplexErp')}
            </h2>
            <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('landing.noComplexErpDesc')}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto">
            <div className="text-center p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-primary" />
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">
                {t('landing.intuitiveInterface')}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t('landing.intuitiveInterfaceDesc')}
              </p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-secondary/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Zap className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-secondary" />
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">
                {t('landing.getStartedMinutes')}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t('landing.getStartedMinutesDesc')}
              </p>
            </div>
            <div className="text-center p-4 sm:p-6 sm:col-span-2 md:col-span-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <Clock className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-accent" />
              </div>
              <h3 className="text-lg sm:text-xl font-heading font-semibold text-foreground mb-2">
                {t('landing.saveTime')}
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground">
                {t('landing.saveTimeDesc')}
              </p>
            </div>
          </div>
          
          {/* YouTube Video CTA */}
          <div className="mt-8 sm:mt-12 lg:mt-16 max-w-3xl mx-auto px-4">
            <a 
              href="https://youtu.be/YDK4StPd2Cs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="group block"
            >
              <div className="relative bg-gradient-to-r from-primary via-purple-600 to-secondary rounded-xl sm:rounded-2xl p-1 shadow-2xl hover:shadow-primary/25 transition-all duration-300 hover:scale-[1.02]">
                <div className="bg-background rounded-lg sm:rounded-xl p-4 sm:p-6 md:p-8 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="relative flex-shrink-0">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <Play className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-white fill-white ml-1" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 sm:w-6 sm:h-6 bg-green-500 rounded-full flex items-center justify-center animate-pulse">
                      <span className="text-white text-xs font-bold">9</span>
                    </div>
                  </div>
                  <div className="text-center sm:text-left flex-1">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-heading font-bold text-foreground mb-1 sm:mb-2">
                      {t('landing.watchVideoTitle')}
                    </h3>
                    <p className="text-sm sm:text-base md:text-lg text-muted-foreground">
                      {t('landing.watchVideoDesc')}
                    </p>
                  </div>
                  <div className="flex-shrink-0 hidden sm:block">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <ArrowRight className="w-6 h-6 text-primary group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">{t('landing.coreFeatures')}</Badge>
            <h2 className="text-4xl font-heading font-extrabold text-foreground mb-4">
              {t('landing.endToEndSolution')}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card 
                  key={index} 
                  className={`group hover:shadow-xl transition-all duration-300 ${feature.highlight ? 'border-primary/30 bg-primary/5' : ''}`}
                >
                  <CardContent className="p-6">
                    <div className={`w-12 h-12 rounded-lg ${feature.highlight ? 'bg-primary/20' : 'bg-muted'} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className={`w-6 h-6 ${feature.color}`} />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge variant="secondary" className="mb-4">{t('landing.features')}</Badge>
              <h2 className="text-4xl font-heading font-extrabold text-foreground mb-6">
                {t('landing.completeControl')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {t('landing.completeControlDesc')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  t('landing.bottomUpCosting'),
                  t('landing.realTimeProfit'),
                  t('landing.smartBooking'),
                  t('landing.complianceTracking'),
                  t('landing.multiCurrency'),
                  t('landing.financialDashboard')
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-primary" />
                    </div>
                    <span className="text-sm text-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&h=400&fit=crop"
                alt="Business analytics"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border bg-card">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="flex flex-col items-start">
              <img 
                src="/tourpilot-logo.png" 
                alt="TourPilot.biz" 
                className="h-14 max-w-[220px] object-contain mb-2"
              />
              <p className="text-xs text-muted-foreground mb-3 max-w-[220px]">
                {t('landing.tagline')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.product')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/#features" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.features')}</a></li>
                <li><a href="/pricing" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.pricing')}</a></li>
                <li><a href="/faq" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.faq')}</a></li>
                <li><a href="/support" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.support')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.company')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.privacyPolicy')}</a></li>
                <li><a href="/cookies-policy" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.cookiesPolicy')}</a></li>
                <li><a href="/terms-of-service" className="text-muted-foreground hover:text-primary transition-colors">{t('footer.termsOfService')}</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-foreground mb-4">{t('footer.contact')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>2DS Finland Oy</li>
                <li>Espoo, Finland</li>
                <li>sales@2ds.fi</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-muted-foreground">
            <p>{t('footer.copyright', { year: new Date().getFullYear() })}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
